import React, { createContext, useState, useContext, useEffect } from 'react';
import {
    onAuthStateChanged,
    signOut,
    User,
    signInAnonymously,
    GoogleAuthProvider,
    signInWithPopup,
    linkWithPopup
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { Database } from '../services/Database';
import { getOptimalOAuthParams, isOAuthStateError, clearFirebaseSessionStorage, withRetry } from '../utils/authUtils';
import { getAuthErrorMessage } from '../utils/getAuthErrorMessage';
import { setUserId, trackLogin, trackLogout } from '../services/analytics';

interface AuthContextType {
    currentUser: User | null;
    loginAsGuest: () => Promise<any>;
    loginWithGoogle: () => Promise<any>;
    upgradeAnonymousWithGoogle: () => Promise<any>;
    logout: () => Promise<void>;
    loading: boolean;
    error: string | null;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const loginAsGuest = async () => {
        try {
            setError(null);
            // 不修改 loading：避免 AuthProvider 隱藏 children 導致白屏，Login 元件自備按鈕 loading 狀態
            const userCredential = await signInAnonymously(auth);
            if (userCredential.user) {
                setUserId(userCredential.user.uid);
                trackLogin('guest');
                localStorage.setItem('user', JSON.stringify({
                    uid: userCredential.user.uid,
                    email: null,
                    displayName: '訪客用戶',
                    isAnonymous: true
                }));
            }
            return userCredential;
        } catch (err: any) {
            console.error('訪客登入失敗:', err);
            const msg = getAuthErrorMessage(err?.message || err?.code || 'default', 'zh-TW');
            setError(msg);
            throw err;
        }
    };

    const loginWithGoogle = async () => {
        try {
            setError(null);
            const provider = new GoogleAuthProvider();
            provider.addScope('email');
            provider.addScope('profile');
            provider.setCustomParameters(getOptimalOAuthParams());

            const userCredential = await withRetry(
                () => signInWithPopup(auth, provider),
                2,
                1000,
                (e) => {
                    if (isOAuthStateError(e)) {
                        clearFirebaseSessionStorage();
                        return true;
                    }
                    return false;
                }
            );

            if (userCredential.user) {
                setUserId(userCredential.user.uid);
                trackLogin('google');
                localStorage.setItem('user', JSON.stringify({
                    uid: userCredential.user.uid,
                    email: userCredential.user.email,
                    displayName: userCredential.user.displayName,
                    emailVerified: userCredential.user.emailVerified
                }));
            }
            return userCredential;
        } catch (err: any) {
            console.error('Google 登入失敗:', err);
            let msg: string;
            if (err?.code === 'auth/popup-blocked') {
                msg = '彈出視窗被阻擋，請允許彈出視窗後再試';
            } else if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/user-cancelled') {
                msg = '登入視窗已關閉，請重新嘗試';
            } else {
                msg = getAuthErrorMessage(err?.message || err?.code || 'default', 'zh-TW');
            }
            setError(msg);
            throw err;
        }
    };

    const upgradeAnonymousWithGoogle = async () => {
        const user = auth.currentUser;
        if (!user || !user.isAnonymous) {
            throw new Error('當前用戶不是匿名用戶');
        }
        try {
            setError(null);
            const provider = new GoogleAuthProvider();
            provider.addScope('email');
            provider.addScope('profile');
            provider.setCustomParameters(getOptimalOAuthParams());

            const userCredential = await linkWithPopup(user, provider);

            if (userCredential.user) {
                setUserId(userCredential.user.uid);
                trackLogin('upgrade_google');
                localStorage.setItem('user', JSON.stringify({
                    uid: userCredential.user.uid,
                    email: userCredential.user.email,
                    displayName: userCredential.user.displayName,
                    emailVerified: userCredential.user.emailVerified,
                    isAnonymous: false
                }));
            }
            return userCredential;
        } catch (err: any) {
            console.error('Google 升級失敗:', err);
            let msg: string;
            if (err?.code === 'auth/credential-already-in-use') {
                msg = '此 Google 帳號已被其他用戶使用，請使用其他 Google 帳號';
            } else if (err?.code === 'auth/email-already-in-use') {
                msg = '此 Google 帳號的電子郵件已被使用，請使用其他 Google 帳號';
            } else if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/user-cancelled') {
                msg = '升級已取消';
            } else if (err?.code === 'auth/popup-blocked') {
                msg = '彈窗被瀏覽器阻擋，請允許彈窗後重試';
            } else {
                msg = getAuthErrorMessage(err?.message || err?.code || 'default', 'zh-TW');
            }
            setError(msg);
            throw new Error(msg);
        }
    };

    const logout = () => {
        trackLogout();
        setUserId(null);
        localStorage.removeItem('user');
        setError(null);
        Database.clearUserSettingsCache();
        return signOut(auth);
    };

    useEffect(() => {
        let isComponentMounted = true;

        // 安全機制：設置超時避免因為某些原因卡在載入狀態
        const authTimeout = setTimeout(() => {
            if (isComponentMounted && loading) {
                console.warn('AuthContext: 認證檢查超時，設置為未登入狀態');
                setCurrentUser(null);
                setLoading(false);
            }
        }, 10000);

        const unsubscribe = onAuthStateChanged(auth, (user: any) => {
            if (!isComponentMounted) return;

            clearTimeout(authTimeout);

            if (user) {
                setUserId(user.uid);
                setCurrentUser(user);
            } else {
                setUserId(null);
                setCurrentUser(null);
                // 登出時清除 Database 的 userSettings 快取，避免下次登入（含換帳號）仍讀到舊資料
                Database.clearUserSettingsCache();
            }

            setLoading(false);
        });

        return () => {
            isComponentMounted = false;
            clearTimeout(authTimeout);
            unsubscribe();
        };
    }, []);

    const value = {
        currentUser,
        loginAsGuest,
        loginWithGoogle,
        upgradeAnonymousWithGoogle,
        logout,
        loading,
        error
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
